<template>
  <v-card
    class="pb-5"
    min-width="1024px">
    <v-card-title>
      <h3 class="text--black">{{ title }}</h3>
    </v-card-title>
    <v-card-actions>
      <v-spacer />
      <v-text-field
        v-model="keyword"
        prepend-icon="mdi-filter"
        dense
        clearable
        label="キーワード検索"
        single-line
        hide-details
        @input="onSearchKeywordChanged">
      </v-text-field>
      <v-spacer />
      <div v-if="$store.state.auth.permission > 0">
        <v-switch
          v-model="onlyMine"
          label=" 自分に関係するデータのみ表示"
          :true-value="1"
          :false-value="0"
          dense
          @change="onSwitchChanged" />
      </div>
    </v-card-actions>
    <v-data-table
      :headers="headers"
      :items="pagination.data"
      :items-per-page="-1"
      :loading="loading"
      :hide-default-footer="true"
      fixed-header
      @click:row="onEditClicked">
      <template
        v-if="!loading"
        #no-data>
        <v-row class="ma-10 pb-10">
          <v-col
            offset="2"
            cols="3"
            class="text-right">
            <v-icon x-large>mdi-message-alert-outline</v-icon>
          </v-col>
          <v-col
            cols="6"
            class="text-left">
            <div
              v-if="keyword || onlyMine"
              class="text--darken-3">
              <h2 class="black--text">データが見つかりませんでした</h2>
              <div class="text--secondary">
                検索条件を変更してください。
              </div>
            </div>
            <div v-else>
              <h2 class="black--text">データがありません</h2>
              <div class="text--secondary">
                既に案件情報を作成済みの場合は協会審査員の承認待ちの可能性があります。<br />
                担当の協会審査員にお問い合わせください。
              </div>
            </div>
          </v-col>
        </v-row>
      </template>
      <template #item.confirmation_approved="{ item }">
        <project-status-badge
          :project="item"
          check-key="confirmation_approved_at" />
      </template>
      <template #item.written_survey_approved="{ item }">
        <project-status-badge
          :project="item"
          check-key="written_survey_approved_at" />
      </template>
      <template #item.field_survey_approved="{ item }">
        <project-status-badge
          :project="item"
          check-key="field_survey_approved_at" />
      </template>
      <template #item.detail_approved="{ item }">
        <project-status-badge
          :project="item"
          check-key="detail_approved_at" />
      </template>
      <template #item.report_approved="{ item }">
        <project-status-badge
          :project="item"
          check-key="report_approved_at" />
      </template>
      <template #item.title="{ item }">
        <div
          class="text-truncate"
          style="max-width: 320px;">
          {{ item.title }}
        </div>
        <div class="">{{ item.facility_name }}</div>
      </template>
      <template #item.approve_user_name="{ item }">
        {{ approvalUserName(item.users) }}
      </template>
      <template #item.updated_at="{ item }">
        {{ formatDate(item.updated_at, 'YYYY/MM/DD HH:mm') }}
      </template>
      <template #item.created_at="{ item }">
        {{ formatDate(item.created_at, 'YYYY/MM/DD HH:mm') }}
      </template>
      <template #item.confirmation_download="{ item }">
        <v-btn
          v-if="item.written_survey_approved_at"
          small
          color="primary"
          @click.stop="onDownloadClicked(item)">
          <v-icon>mdi-download</v-icon>確認表DL
        </v-btn>
      </template>
      <template #item.sorting_download="{ item }">
        <v-btn
          v-if="item.written_survey_approved_at"
          small
          color="primary"
          @click.stop="onSortingDownloadClicked(item)">
          <v-icon>mdi-download</v-icon>同一範囲の整理表DL
        </v-btn>
      </template>
      <template #item.detail_download="{ item }">
        <v-btn
          v-if="item.detail_approved_at"
          small
          color="success"
          @click.stop="onDetailDownloadClicked(item)">
          <v-icon>mdi-download</v-icon>詳細表DL
        </v-btn>
      </template>
      <template #item.report_download="{ item }">
        <v-btn
          v-if="item.report_approved_at"
          small
          color="primary"
          @click.stop="onReportDownloadClicked(item)">
          <v-icon>mdi-download</v-icon>報告書DL
        </v-btn>
      </template>
      <template
        v-if="$store.state.auth.permission >= 2"
        #item.actions="{ item }">
        <v-btn
          small
          color="error"
          @click.stop="onDeletionButtonClicked(item)">
          <v-icon>mdi-delete</v-icon>削除
        </v-btn>
      </template>
      <template #footer>
        <footer-pagination
          v-model="pagination"
          @change="onPaginationChanged" />
      </template>
    </v-data-table>
    <confirm-dialog
      :value="deletionConfirmDialog"
      title="削除確認"
      ok-text="削除する"
      cancel-text="キャンセル"
      @ok="onDeletionExecuteClicked"
      @cancel="deletionConfirmDialog = false"
      @input="deletionConfirmDialog = false">
      <v-card-text>
        <div
          v-if="deletionTargetItem"
          class="mb-3 font-weight-bold">
          案件名「{{ deletionTargetItem.title }}」
        </div>
        このデータを削除します。<br />この操作は元に戻せません。
      </v-card-text>
    </confirm-dialog>
    <v-alert
      class="ma-10 pa-3"
      light
      outlined>
      <v-row dense>
        <v-col cols="1">
          <v-chip
            color="warning"
            small>
            <v-icon>mdi-pencil</v-icon>
            入力待
          </v-chip>
        </v-col>
        <v-col cols="11">
          <div class="text-subtitle-2">
            主任調査員による入力作業が行われていません。
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="$store.state.has_approval_authority"
        dense>
        <v-col cols="1">
          <v-chip
            color="warning"
            small>
            <v-icon>mdi-clock</v-icon>
            承認待
          </v-chip>
        </v-col>
        <v-col cols="11">
          <div class="text-subtitle-2">
            主任調査員の入力作業が完了し、協会審査員のチェック待ちです。
          </div>
        </v-col>
      </v-row>

      <v-row
        v-else
        dense>
        <v-col cols="1">
          <v-chip
            color="primary"
            small>
            <v-icon>mdi-email-send</v-icon>
            申請中
          </v-chip>
        </v-col>
        <v-col cols="11">
          <div class="text-subtitle-2">
            主任調査員の入力作業が完了し、協会審査員のチェック待ちです。
          </div>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="1">
          <v-chip
            color="success"
            small>
            <v-icon>mdi-check</v-icon>
            承認済
          </v-chip>
        </v-col>
        <v-col cols="11">
          <div class="text-subtitle-2">
            入力内容はチェック済です。次の作業に進んでください。
          </div>
        </v-col>
      </v-row>
    </v-alert>
  </v-card>
</template>

<script>
import FooterPagination from './FooterPagination'
import downloadFile from '../logic/download'
import ProjectStatusBadge from './ProjectStatusBadge'
import ConfirmDialog from './ConfirmDialog'

export default {
  name: 'ProjectTable',
  components: { ConfirmDialog, ProjectStatusBadge, FooterPagination },
  props: {
    title: {
      type: String,
      default: null,
    },
    headers: {
      type: Array,
      default: () => ([]),
    },
    endpoint: {
      type: String,
      default: '/api/confirmations',
    },
  },
  data: () => ({
    items: [],
    loading: true,
    keyword: '',
    projectId: '',
    onlyMine: false,
    pagination: { data: [] },
    currentPage: 1,
    timer: null,
    deletionConfirmDialog: false,
    deletionTargetItem: null,
  }),
  mounted() {
    this.pagination.current_page = this.$route.query.page ?? 0
    this.keyword = this.$route.query.keyword ?? ''
    this.projectId = this.$route.query.project_id ?? ''
    this.onlyMine = parseInt(this.$route.query.only_mine, 10) ? 1 : 0
    this.pagination.per_page = this.$route.query.count
      ?? window.localStorage.getItem(`pagination-count-${this.name}`)
      ?? '50'
    this.fetchItems()
  },
  destroyed() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  },
  methods: {
    approvalUserName(users) {
      const approvalUser = users.filter(user => user.pivot.role_id === 2)
      return approvalUser.length > 0
        ? (approvalUser.map(u => u.name).join('、') ?? '')
        : ''
    },
    async fetchItems(page = 0) {
      this.loading = true
      const params = {
        page,
        project_id: this.projectId,
        keyword: this.keyword,
        only_mine: this.onlyMine,
        count: this.pagination.per_page,
      }
      const { data } = await window.axios.get(this.endpoint, { params })
      this.pagination = data
      this.loading = false

      this.$router.replace({
        name: this.$route.name,
        query: params,
      }, () => {}, () => {})
    },
    onSearchKeywordChanged() {
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(this.fetchItems, 500)
    },
    onSwitchChanged() {
      this.fetchItems(0)
    },
    onPaginationChanged(pagination) {
      this.pagination = pagination
      this.fetchItems(pagination.current_page)
      window.localStorage.setItem(`pagination-count-${this.name}`, this.pagination.per_page)
    },
    onEditClicked(item) {
      this.$emit('click:row', item)
    },
    onDownloadClicked(item) {
      const url = `${this.endpoint}/${item.id}/download`
      downloadFile(url)
    },
    onSortingDownloadClicked(item) {
      const url = `/api/details/${item.id}/download-excel`
      downloadFile(url)
    },
    onDetailDownloadClicked(item) {
      const url = `/api/details/${item.id}/download-detail`
      downloadFile(url)
    },
    onReportDownloadClicked(item) {
      const url = `/api/reports/${item.id}/download-report`
      downloadFile(url)
    },
    onDeletionButtonClicked(item) {
      this.deletionTargetItem = item
      this.deletionConfirmDialog = true
    },
    async onDeletionExecuteClicked() {
      this.deletionConfirmDialog = false
      await window.axios.delete(`${this.endpoint}/${this.deletionTargetItem.id}`)
      await this.fetchItems(this.pagination.current_page)
    },
  },
}
</script>
